import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { cartLineKey } from '../catalog';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cartLines, setCartLines] = useState([]);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showAddedToast = useCallback(() => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToast('Added to cart');
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 2600);
  }, []);

  const addToCart = useCallback(
    (product, weight, pieces) => {
      const p = Math.max(1, pieces);
      const key = cartLineKey(product.id, weight);
      setCartLines((prev) => {
        const idx = prev.findIndex((line) => line.key === key);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], pieces: next[idx].pieces + p };
          return next;
        }
        return [
          ...prev,
          {
            key,
            productId: product.id,
            name: product.name,
            image: product.image,
            weight,
            pieces: p,
          },
        ];
      });
      showAddedToast();
    },
    [showAddedToast]
  );

  const updateLinePieces = useCallback((key, nextPieces) => {
    const n = Math.max(1, nextPieces);
    setCartLines((prev) => prev.map((line) => (line.key === key ? { ...line, pieces: n } : line)));
  }, []);

  const changeLineWeight = useCallback((key, newWeight) => {
    setCartLines((prev) => {
      const line = prev.find((l) => l.key === key);
      if (!line || line.weight === newWeight) return prev;
      const newKey = cartLineKey(line.productId, newWeight);
      const without = prev.filter((l) => l.key !== key);
      const existing = without.find((l) => l.key === newKey);
      if (existing) {
        return without.map((l) =>
          l.key === newKey ? { ...l, pieces: l.pieces + line.pieces } : l
        );
      }
      return [...without, { ...line, key: newKey, weight: newWeight }];
    });
  }, []);

  const removeLine = useCallback((key) => {
    setCartLines((prev) => prev.filter((line) => line.key !== key));
  }, []);

  const value = useMemo(
    () => ({
      cartLines,
      addToCart,
      updateLinePieces,
      changeLineWeight,
      removeLine,
      cartLineCount: cartLines.length,
    }),
    [cartLines, addToCart, updateLinePieces, changeLineWeight, removeLine]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
      {toast ? (
        <div className="toast-banner" role="status" aria-live="polite">
          {toast}
        </div>
      ) : null}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within CartProvider');
  }
  return ctx;
}
